import {
  RowActionTiers,
  Tier1RowActions,
  Tier2RowActions,
  Tier3RowActions,
  Tier4RowActions,
  Tier5RowActions,
  Tier6RowActions,
} from '@openx/constants/rowActions';
import {
  type AllTableCriteria,
  type ChangeCriteriaHandler,
  type ChangeSingleCriteriaAction,
  type CriteriaChange,
  CriteriaDimension,
  type PaginationCriteria,
  type SortCriteria,
  type SortDirection,
  type TableCriteria,
} from '@openx/types/tableCriteria';
import type { BaseRow, RowActionProps } from '@openx/types/tableHelpers';

export function currentSort(column: string, sortConfig?: SortCriteria): SortDirection | undefined {
  if (!sortConfig || sortConfig.column !== column) {
    return undefined;
  }

  return sortConfig.direction;
}

export function hasPrevItems(paginationCriteria: PaginationCriteria): boolean {
  return paginationCriteria.pageNumber > 1;
}

export function maxPageNumber(paginationCriteria: PaginationCriteria): number {
  return Math.max(Math.ceil(paginationCriteria.totalCount / paginationCriteria.pageSize), 1);
}

export function hasNextItems(paginationCriteria: PaginationCriteria): boolean {
  return maxPageNumber(paginationCriteria) > paginationCriteria.pageNumber || paginationCriteria.hasMore === true;
}

export function prepareChangeCriteriaHandler<
  D extends CriteriaDimension,
  FiltersT extends Record<string, unknown> = Record<string, unknown>,
>(dimension: D, onChange?: ChangeCriteriaHandler<CriteriaDimension, FiltersT>): ChangeSingleCriteriaAction<D> {
  return (newCriteria: TableCriteria<D>) => {
    onChange?.({
      dimension,
      value: newCriteria,
    } as CriteriaChange<D, FiltersT>);
  };
}

export function hasToLoadMore(
  requestPageNumber: number,
  pagination?: TableCriteria<typeof CriteriaDimension.PAGINATION>
): boolean {
  if (!pagination) {
    return false;
  }

  return requestPageNumber > maxPageNumber(pagination) && hasNextItems(pagination);
}

export function adjustPaginationCriteria<FiltersT extends {}>(
  criteria: AllTableCriteria<FiltersT>,
  totalCount: number,
  hasMore?: boolean
): AllTableCriteria<FiltersT> {
  const previousPaginationCriteria = criteria[CriteriaDimension.PAGINATION];

  if (!previousPaginationCriteria) {
    return criteria;
  }

  const pageNumber =
    hasMore === false
      ? Math.min(maxPageNumber({ ...previousPaginationCriteria, totalCount }), previousPaginationCriteria.pageNumber)
      : previousPaginationCriteria.pageNumber;

  return {
    ...criteria,
    [CriteriaDimension.PAGINATION]: {
      ...previousPaginationCriteria,
      hasMore,
      pageNumber,
      totalCount,
    },
  };
}

type TierRowActions<T extends BaseRow> = {
  [RowActionTiers.Tier1]?: RowActionProps<T>[];
  [RowActionTiers.Tier2]?: RowActionProps<T>[];
  [RowActionTiers.Tier3]?: RowActionProps<T>[];
  [RowActionTiers.Tier4]?: RowActionProps<T>[];
  [RowActionTiers.Tier5]?: RowActionProps<T>[];
  [RowActionTiers.Tier6]?: RowActionProps<T>[];
};

type GenericRowActions<T extends BaseRow> = Record<string, RowActionProps<T>>;

function applyCustomSortingCriteria<T extends BaseRow>(
  rowActions: GenericRowActions<T>,
  excludedValues?: RowActionProps<T>[]
): GenericRowActions<T>[] {
  const customSortingOrder: string[] = [
    Tier4RowActions.Activate,
    Tier4RowActions.Deactivate,
    Tier4RowActions.Unpublish,
    Tier4RowActions.Pause,
    Tier4RowActions.Expire,
    Tier4RowActions.Pending,
    Tier4RowActions.Reactivate,
    Tier4RowActions.Resume,
    Tier4RowActions.Clone,
    Tier4RowActions.UseAsTemplate,
    Tier4RowActions.UseSharedTargeting,
    Tier4RowActions.Override,
    Tier4RowActions.Impersonate,
    Tier4RowActions.Troubleshoot,
    Tier4RowActions.ResetPassword,
    Tier4RowActions.Logout,
  ];

  const sortedRowActions: GenericRowActions<T>[] = [];

  Object.keys(rowActions).forEach(key => {
    if (!excludedValues?.includes(rowActions[key])) {
      sortedRowActions.push({ [key]: rowActions[key] });
    }
  });

  return sortedRowActions.sort(
    (a: GenericRowActions<T>, b: GenericRowActions<T>) =>
      customSortingOrder.indexOf(Object.keys(a)[0]) - customSortingOrder.indexOf(Object.keys(b)[0])
  );
}

function categorizeRowAction<T extends BaseRow>(
  rowAction: GenericRowActions<T>,
  excludedValues?: RowActionProps<T>[]
): TierRowActions<T> {
  const sortedRowActions = applyCustomSortingCriteria(rowAction, excludedValues);

  const tiers: TierRowActions<T> = sortedRowActions.reduce(
    (acc, action) => {
      const key = Object.keys(action)[0];

      switch (key) {
        case Tier1RowActions.Rename:
        case Tier1RowActions.Details:
          acc[RowActionTiers.Tier1] = [action[key], ...(acc[RowActionTiers.Tier1] || [])];
          break;
        case key.match(new RegExp(Tier2RowActions.Add))?.input:
          acc[RowActionTiers.Tier2] = [...(acc[RowActionTiers.Tier2] || []), action[key]];
          break;
        case Tier3RowActions.Delete:
        case Tier3RowActions.Edit:
          acc[RowActionTiers.Tier3] = [action[key], ...(acc[RowActionTiers.Tier3] || [])];
          break;
        case Tier5RowActions.ManageSignalControl:
        case Tier5RowActions.ShowSimilar:
        case Tier5RowActions.CreateLaLSegments:
          acc[RowActionTiers.Tier5] = [...(acc[RowActionTiers.Tier5] || []), action[key]];
          break;
        case Tier6RowActions.CheckTargeting:
          acc[RowActionTiers.Tier6] = [...(acc[RowActionTiers.Tier6] || []), action[key]];
          break;
        default:
          // Tier4 is default
          acc[RowActionTiers.Tier4] = [...(acc[RowActionTiers.Tier4] || []), action[key]];
          break;
      }

      return acc;
    },
    {} as TierRowActions<T>
  );

  return tiers;
}

export function sortMenuButtonItems<T extends BaseRow>(
  rowActions: GenericRowActions<T>,
  excludedValues?: RowActionProps<T>[]
): RowActionProps<T>[] {
  const tiers = categorizeRowAction(rowActions, excludedValues);

  const sortedActions: RowActionProps<T>[] = [];

  Object.values(RowActionTiers).forEach(tierType => {
    const tierActions = tiers[tierType];
    if (tierActions) {
      if (sortedActions.length > 0) {
        tierActions[0].topDivider = true;
      }
      sortedActions.push(...tierActions);
    }
  });

  return sortedActions;
}
